import { createStore } from "vuex";
import CustomerStore from "@/modules/customer/store";
import AddressStore from "@/modules/address/store";
import cart from "@/modules/cart/store/index";
import PartnerService from "@/services/partner/partner.service";
import CookieService from "@/services/policy/cookie.service";
import GoogleService from "@/services/tracking/google.service";
import auth from "@/modules/auth/store/auth";
import product from "@/modules/product/store/products"; // Certifique-se de que o caminho está correto
import designSystem from "@/modules/designSystem/store";
import onboarding from "@/modules/onboarding/store";

function formatId(rawId) {
  return String(rawId ?? "")
    .trim()
    .toLowerCase();
}
function isInvalidCatalogPdvId(pdvId) {
  if (pdvId == null || String(pdvId).trim() === "") return false;
  return formatId(pdvId) === "payment";
}
function isInvalidCatalogCustomerId(customerId) {
  if (customerId == null || String(customerId).trim() === "") return false;
  const formatted = formatId(customerId);
  return formatted === "success" || formatted === "card";
}

const store = createStore({
  state: {
    isCookiePolicyAccepted: false,
    pdvId: null,
    customerId: null,
  },
  mutations: {
    setCookiePolicyStatus(state, status) {
      state.isCookiePolicyAccepted = status;
    },
    SET_PDV_ID(state, pdvId) {
      state.pdvId = pdvId;
      if (pdvId) {
        localStorage.setItem("pdvId", pdvId);
      } else {
        localStorage.removeItem("pdvId");
        sessionStorage.removeItem("pdvId");
      }
    },

    SET_CUSTOMER_ID(state, customerId) {
      state.customerId = customerId;
      if (customerId) {
        localStorage.setItem("customerId", customerId);
      } else {
        localStorage.removeItem("customerId");
        sessionStorage.removeItem("customerId");
      }
    },

    CLEAR_PARAMS(state) {
      state.pdvId = null;
      state.customerId = null;
      localStorage.removeItem("pdvId");
      localStorage.removeItem("customerId");
      sessionStorage.removeItem("pdvId");
      sessionStorage.removeItem("customerId");
    },

    RESTORE_FROM_STORAGE(state) {
      let pdvId = localStorage.getItem("pdvId");
      let customerId = localStorage.getItem("customerId");

      if (isInvalidCatalogPdvId(pdvId)) {
        localStorage.removeItem("pdvId");
        sessionStorage.removeItem("pdvId");
        pdvId = null;
      }
      if (isInvalidCatalogCustomerId(customerId)) {
        localStorage.removeItem("customerId");
        sessionStorage.removeItem("customerId");
        customerId = null;
      }

      state.pdvId = pdvId || null;
      state.customerId = customerId || null;
    },
  },
  actions: {
    setPartner(context, partner) {
      if (!partner) {
        return;
      }

      PartnerService.setActivePartner(partner);
      context.commit("startPartnerTracking");
    },
    startPartnerTracking({ getters }) {
      const analyticTag = getters.getPartnerConfiguration?.analyticTag || null;
      const analyticCategory =
        getters.getPartnerConfiguration?.analyticCategory || null;

      if (!analyticTag || !analyticCategory) {
        // Corrigido
        return;
      }

      GoogleService.startTracking(analyticTag, analyticCategory);
    },
    acceptCookiePolicy({ commit }) {
      return new Promise((resolve) => {
        CookieService.setAcceptedCookie();
        commit("setCookiePolicyStatus", true);
        resolve();
      });
    },
    setPdvId({ commit }, pdvId) {
      commit("SET_PDV_ID", pdvId);
    },

    setCustomerId({ commit }, customerId) {
      commit("SET_CUSTOMER_ID", customerId);
    },

    setParams({ commit }, { pdvId, customerId = null }) {
      const safePdv = isInvalidCatalogPdvId(pdvId) ? null : pdvId;
      const safeCustomer = isInvalidCatalogCustomerId(customerId)
        ? null
        : customerId;
      commit("SET_PDV_ID", safePdv);
      commit("SET_CUSTOMER_ID", safeCustomer);
    },

    clearParams({ commit }) {
      commit("CLEAR_PARAMS");
    },

    restoreFromStorage({ commit }) {
      commit("RESTORE_FROM_STORAGE");
    },
  },
  getters: {
    getPartner: () =>
      PartnerService.getActivePartner() || PartnerService.getDefaultPartner(),
    getPartnerConfiguration: (state, getters) =>
      PartnerService.getPartnerConfiguration(getters.getPartner),
    getCookiesPolicyAccepted: (state) => state.isCookiePolicyAccepted,
    getPhoneNumber: () => process.env.VUE_APP_WHATSAPP_PHONE_NUMBER,
    getPdvId: (state) => state.pdvId,
    getCustomerId: (state) => state.customerId,
    hasValidPdvId: (state) => !!state.pdvId,
    getParams: (state) => ({
      pdvId: state.pdvId,
      customerId: state.customerId,
    }),
  },
  modules: {
    namespaced: true,
    auth, // Módulo de autenticação
    product, // Módulo de produtos
    customer: CustomerStore,
    address: AddressStore,
    cart,
    designSystem,
    onboarding,
  },
});

export default store;
